import React, { useState } from 'react';
import { useProfile } from '../contexts/ProfileContext';
import PremiumThemes from './PremiumThemes';
import { FiSun, FiMoon, FiZap, FiSave, FiEye, FiStar } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const ThemeSelector = () => {
  const { profile, saveProfile, loading } = useProfile();
  const [selectedTheme, setSelectedTheme] = useState(profile?.theme || 'minimal-light');
  const [saving, setSaving] = useState(false);
  const [showPremium, setShowPremium] = useState(false);

  const basicThemes = [
    {
      id: 'minimal-light', name: 'Minimal Light', description: 'Clean and professional light theme', icon: FiSun,
      preview: { bg: 'bg-white', text: 'text-gray-900', accent: 'bg-indigo-600', border: 'border-gray-200' }
    },
    {
      id: 'dark-pro', name: 'Dark Pro', description: 'Sleek dark theme for professionals', icon: FiMoon,
      preview: { bg: 'bg-gray-900', text: 'text-white', accent: 'bg-indigo-500', border: 'border-gray-700' }
    },
    {
      id: 'cyber-neon', name: 'Cyber Neon', description: 'Futuristic neon green theme', icon: FiZap,
      preview: { bg: 'bg-black', text: 'text-green-400', accent: 'bg-cyan-400', border: 'border-green-500' }
    },
  ];

  const handleSave = async () => {
    setSaving(true);
    const result = await saveProfile({ theme: selectedTheme });
    if (result.success) {
      alert('Theme saved successfully!');
    } else {
      alert(`Error saving theme: ${result.error}`);
    }
    setSaving(false);
  };

  const ThemePreview = ({ theme }) => (
    <div className={`p-4 rounded-lg border-2 ${theme.preview.border} ${theme.preview.bg} transition-all duration-200`}>
      {/* Mock profile header */}
      <div className="flex items-center space-x-3 mb-4">
        <div className={`w-12 h-12 ${theme.preview.accent} rounded-full`}></div>
        <div>
          <div className={`h-3 w-20 ${theme.preview.text} bg-current opacity-80 rounded mb-1`}></div>
          <div className={`h-2 w-16 ${theme.preview.text} bg-current opacity-50 rounded`}></div>
        </div>
      </div>
      {/* Mock content blocks */}
      <div className="space-y-2">
        <div className={`h-8 ${theme.preview.border} border rounded`}></div>
        <div className={`h-6 ${theme.preview.border} border rounded`}></div>
        <div className={`h-10 ${theme.preview.border} border rounded`}></div>
      </div>
    </div>
  );

  if (showPremium) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900 mb-2">Premium Themes</h3>
          <button onClick={() => setShowPremium(false)} className="text-indigo-600 hover:text-indigo-700 text-sm font-medium">
            ← Back to Basic Themes
          </button>
        </div>
        <PremiumThemes profile={profile} theme={{}} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Choose Your Theme</h3>
          <p className="text-gray-600">Select a theme that matches your style and brand</p>
        </div>
        <button
          onClick={() => setShowPremium(true)}
          className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-yellow-400 to-orange-400 text-white rounded-lg font-medium hover:from-yellow-500 hover:to-orange-500 transition-colors"
        >
          <SafeIcon icon={FiStar} className="mr-2" />
          View Premium Themes
        </button>
      </div>

      {/* Basic Themes */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {basicThemes.map((theme) => (
          <div
            key={theme.id}
            className={`relative cursor-pointer rounded-xl border-2 transition-all duration-200 ${
              selectedTheme === theme.id
                ? 'border-indigo-500 ring-2 ring-indigo-200'
                : 'border-gray-200 hover:border-gray-300'
            }`}
            onClick={() => setSelectedTheme(theme.id)}
          >
            {/* Selection indicator */}
            {selectedTheme === theme.id && (
              <div className="absolute -top-2 -right-2 w-6 h-6 bg-indigo-600 text-white rounded-full flex items-center justify-center text-sm">
                ✓
              </div>
            )}
            {/* Theme preview */}
            <ThemePreview theme={theme} />
            {/* Theme info */}
            <div className="p-4 border-t border-gray-200">
              <div className="flex items-center space-x-2 mb-2">
                <SafeIcon icon={theme.icon} className="text-gray-600" />
                <h4 className="font-medium text-gray-900">{theme.name}</h4>
              </div>
              <p className="text-sm text-gray-600">{theme.description}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Current Theme Info */}
      <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
        <div className="flex items-center space-x-2 mb-2">
          <SafeIcon icon={FiEye} className="text-indigo-600" />
          <h4 className="font-medium text-indigo-900">Current Theme</h4>
        </div>
        <p className="text-indigo-700">
          {basicThemes.find(t => t.id === selectedTheme)?.name || 'Unknown Theme'}
        </p>
        <p className="text-sm text-indigo-600 mt-1">
          {basicThemes.find(t => t.id === selectedTheme)?.description}
        </p>
      </div>

      {/* Save Button */}
      <div className="flex justify-end pt-6 border-t border-gray-200">
        <button
          onClick={handleSave}
          disabled={saving || loading || selectedTheme === profile?.theme}
          className="inline-flex items-center px-6 py-3 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <SafeIcon icon={FiSave} className="mr-2" />
          {saving ? 'Saving...' : 'Save Theme'}
        </button>
      </div>
    </div>
  );
};

export default ThemeSelector;